import { useRef } from 'react';

import {
  Button,
  HStack,
  IconButton,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Spacer,
  Stack,
  Text,
} from '@chakra-ui/react';

import { CloseCircleOutline } from '@aslanpay/portal-common-lib';

import Step from 'app/components/UI/Step';

import useStep from 'app/hooks/useStep';

import { useTopUpWalletStore } from 'app/store'; // useManagingCompanyStore,

import useFetchCompany from 'app/hooks/useFetchCompany';
import useAuthContext from 'app/hooks/useAuthContext';
import { useParams } from 'react-router-dom';

import { GiftingTopUpWalletForm, GiftingConfirmTopUpWalletForm } from '../GiftingTopUpForm';

import { GiftingTopUpFormFields, GiftingTopUpFormProps } from '../GiftingTopUpForm/types';

function GiftingTopUpModal({ isOpen, onClose, onSuccess }: GiftingTopUpFormProps) {
  const { companyId } = useParams();
  const { user } = useAuthContext();
  const { data: company } = useFetchCompany(companyId || user?.companyId);

  const setTopUpWalletData = useTopUpWalletStore((state) => state.setTopUpWalletData);
  const resetTopUpWalletData = useTopUpWalletStore((state) => state.resetTopUpWalletData);

  const submitButtonRef = useRef<HTMLElement>(null);

  const { topUpWalletData } = useTopUpWalletStore.getState();

  if (isOpen && company?.id && !(topUpWalletData as GiftingTopUpFormFields)) {
    setTopUpWalletData(topUpWalletData);
  }

  const steps = [
    {
      id: 'gifting-topup-form',
      name: 'Select amount',
    },
    {
      id: 'gifting-confirm-form',
      name: 'Confirm',
    },
  ];

  const { currentStep, nextStep, previousStep, canPrevStep, resetSteps } = useStep(steps.length);

  const handleClose = () => {
    resetTopUpWalletData();
    resetSteps();
    onClose();
  };

  const renderCurrentStepForm = () => {
    switch (steps[currentStep].id) {
      case 'gifting-confirm-form':
        return (
          <GiftingConfirmTopUpWalletForm
            id={steps[currentStep].id}
            ref={submitButtonRef}
            onStepAction={handleClose}
            onSuccess={onSuccess}
            previousStep={previousStep}
            canPrevStep={canPrevStep}
          />
        );
      case 'gifting-topup-form':
      default:
        return (
          <GiftingTopUpWalletForm
            id={steps[currentStep].id}
            ref={submitButtonRef}
            onStepAction={nextStep}
            previousStep={previousStep}
            canPrevStep={canPrevStep}
          />
        );
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      closeOnEsc={false}
      closeOnOverlayClick={false}
      onClose={handleClose}
      isCentered
    >
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          <HStack>
            <Text as="h6">Top up wallet</Text>
            <Spacer />
            <IconButton
              variant="link"
              icon={<CloseCircleOutline boxSize="30px" />}
              aria-label="Close top up wallet"
              onClick={handleClose}
            />
          </HStack>
        </ModalHeader>
        <ModalBody>
          <Stack spacing="40px">
            <HStack spacing="0" flexGrow={1} alignItems="flex-start">
              {steps.map((step, id) => (
                <Step
                  key={step.name}
                  number={id + 1}
                  name={step.name}
                  isActive={currentStep === id}
                  isCompleted={currentStep > id}
                  isFirstStep={id === 0}
                  isLastStep={steps.length === id + 1}
                />
              ))}
            </HStack>
            {renderCurrentStepForm()}
          </Stack>
        </ModalBody>
        <ModalFooter ref={submitButtonRef}>
          {currentStep !== steps.length - 1 && (
            <Text color="system.error" textStyle="extraSmallMedium">
              * mandatory
            </Text>
          )}
          <Spacer />
          <Button mr="20px" variant="link" size="sm" onClick={handleClose}>
            Cancel
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}

export default GiftingTopUpModal;
